import { useState, useEffect, useCallback } from 'react';

export interface UISettings {
  leftSidebarOpen: boolean;
  rightSidebarOpen: boolean;
  theme: 'light' | 'dark' | 'system';
  language: string;
}

const DEFAULT_UI_SETTINGS: UISettings = {
  leftSidebarOpen: false,
  rightSidebarOpen: false,
  theme: 'system',
  language: 'en',
};

const STORAGE_KEY = 'ui-settings';

export const useSettings = () => {
  const [uiSettings, setUISettings] = useState<UISettings>(DEFAULT_UI_SETTINGS);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load settings from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsedSettings = JSON.parse(stored);
        setUISettings({ ...DEFAULT_UI_SETTINGS, ...parsedSettings });
      }
    } catch (error) {
      console.error('Failed to load UI settings:', error);
    } finally {
      setIsLoaded(true);
    }
  }, []);

  // Save settings to localStorage
  const saveUISettings = useCallback((newSettings: Partial<UISettings>) => {
    try {
      const updatedSettings = { ...uiSettings, ...newSettings };
      setUISettings(updatedSettings);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedSettings));
    } catch (error) {
      console.error('Failed to save UI settings:', error);
    }
  }, [uiSettings]);

  // Reset settings to default
  const resetSettings = useCallback(() => {
    setUISettings(DEFAULT_UI_SETTINGS);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  return {
    uiSettings,
    isLoaded,
    saveUISettings,
    resetSettings,
  };
}; 